import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Text,
  Badge,
} from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Switch } from '@chakra-ui/react'
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogBackdrop,
  DialogPositioner,
  DialogTitle,
  DialogCloseTrigger,
} from '@chakra-ui/react'
import { Folder, ChevronDown, Lock } from 'lucide-react'
import { createToaster } from '@chakra-ui/react'
import { useCreateFolder, useUpdateFolder, useFolder, useFolders, useFolderPath } from '../hooks/useFolders'
import type { DocumentCategory, DocumentFolder } from '../types'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

const CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'constitutif', label: 'Documents constitutifs' },
  { value: 'ag', label: 'Assemblées générales' },
  { value: 'comptabilite', label: 'Comptabilité' },
  { value: 'travaux', label: 'Travaux' },
  { value: 'technique', label: 'Diagnostics et technique' },
  { value: 'contrats', label: 'Contrats et prestataires' },
  { value: 'sinistres', label: 'Sinistres et assurances' },
  { value: 'juridique', label: 'Juridique' },
  { value: 'correspondance', label: 'Correspondance' },
  { value: 'coproprietaire', label: 'Copropriétaires' },
]

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

interface FolderModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  parentFolderId?: string
  editFolderId?: string
  mode: 'create' | 'edit'
}

export function FolderModal({
  isOpen,
  onClose,
  residenceId,
  parentFolderId: initialParentFolderId,
  editFolderId,
  mode,
}: FolderModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [parentFolderId, setParentFolderId] = useState<string | undefined>(initialParentFolderId)
  const [isSystemFolder, setIsSystemFolder] = useState(false)
  const [suggestedCategory, setSuggestedCategory] = useState<DocumentCategory | undefined>()

  const createFolder = useCreateFolder()
  const updateFolder = useUpdateFolder()
  const { data: folderToEdit } = useFolder(editFolderId)
  const { data: residenceFolders = [] } = useFolders(residenceId)
  const { data: parentPath } = useFolderPath(parentFolderId)

  // Get agency_id from the selected residence
  const { data: selectedResidence } = useQuery({
    queryKey: ['residence', residenceId],
    queryFn: async () => {
      if (!residenceId) return null
      const { data, error } = await supabase
        .from('residences')
        .select('agency_id')
        .eq('id', residenceId)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!residenceId,
  })

  // Load agency-level folders (co-owner folders with residence_id = NULL)
  const { data: agencyFolders = [] } = useQuery({
    queryKey: ['agency-folders', selectedResidence?.agency_id],
    queryFn: async () => {
      if (!selectedResidence?.agency_id) return []

      const { data, error } = await supabase
        .from('document_folders')
        .select('*')
        .eq('agency_id', selectedResidence.agency_id)
        .is('residence_id', null)
        .is('deleted_at', null)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true })

      if (error) {
        console.error('❌ AGENCY FOLDERS ERROR:', error)
        throw error
      }
      return data as DocumentFolder[]
    },
    enabled: !!selectedResidence?.agency_id,
    staleTime: 5 * 60 * 1000,
  })

  // Combine residence folders and co-owner folders with virtual hierarchy
  const folders = useMemo(() => {
    // Find the residence-level "Copropriétaires" folder
    const residenceCopropsFolder = residenceFolders.find(
      (f) => f.slug === 'coproprietaires' || f.suggested_category === 'coproprietaire'
    )

    // Extract co-owner physical folders (with owner_id)
    const coOwnerFolders = agencyFolders.filter((f) => f.owner_id)

    // If we have a residence "Copropriétaires" folder, remap co-owner folders to be its children
    if (residenceCopropsFolder && coOwnerFolders.length > 0) {
      // Remap co-owner folders to have the residence "Copropriétaires" as parent
      const remappedCoOwnerFolders = coOwnerFolders.map((f) => ({
        ...f,
        parent_folder_id: residenceCopropsFolder.id,
      }))

      return [...residenceFolders, ...remappedCoOwnerFolders]
    }

    // Fallback: just combine everything
    return [...residenceFolders, ...coOwnerFolders]
  }, [residenceFolders, agencyFolders])

  // Récupérer les catégories déjà utilisées (dossiers système existants)
  const usedCategories = folders
    .filter((f) => f.suggested_category && f.id !== editFolderId)
    .map((f) => f.suggested_category)

  // Charger les données du dossier à éditer ou réinitialiser
  useEffect(() => {
    if (mode === 'edit' && folderToEdit) {
      setName(folderToEdit.name)
      setDescription(folderToEdit.description || '')
      setIsSystemFolder(!!folderToEdit.suggested_category)
      setSuggestedCategory(folderToEdit.suggested_category || undefined)
    } else {
      setName('')
      setDescription('')
      setParentFolderId(initialParentFolderId)
      setIsSystemFolder(false)
      setSuggestedCategory(undefined)
    }
  }, [mode, folderToEdit, isOpen, initialParentFolderId])

  const handleClose = () => {
    setName('')
    setDescription('')
    setParentFolderId(undefined)
    setIsSystemFolder(false)
    setSuggestedCategory(undefined)
    onClose()
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      toaster.error({
        title: 'Erreur',
        description: 'Le nom du dossier est obligatoire',
      })
      return
    }

    if (isSystemFolder && !suggestedCategory) {
      toaster.error({
        title: 'Erreur',
        description: 'Veuillez sélectionner une catégorie pour ce dossier système',
      })
      return
    }

    try {
      if (mode === 'create') {
        await createFolder.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          residence_id: residenceId,
          parent_folder_id: parentFolderId,
          suggested_category: isSystemFolder ? suggestedCategory : undefined,
          is_system_folder: isSystemFolder,
        })

        toaster.success({
          title: 'Dossier créé',
          description: `Le dossier "${name}" a été créé avec succès${isSystemFolder ? ' (dossier système)' : ''}`,
        })
      } else if (editFolderId) {
        await updateFolder.mutateAsync({
          id: editFolderId,
          updates: {
            name: name.trim(),
            description: description.trim() || undefined,
            suggested_category: isSystemFolder ? suggestedCategory : null,
            is_system_folder: isSystemFolder,
          },
        })

        toaster.success({
          title: 'Dossier mis à jour',
          description: `Le dossier "${name}" a été mis à jour`,
        })
      }

      handleClose()
    } catch (error) {
      console.error('Folder operation error:', error)
      toaster.error({
        title: 'Erreur',
        description:
          error instanceof Error
            ? error.message
            : mode === 'create'
            ? 'Erreur lors de la création du dossier'
            : 'Erreur lors de la mise à jour du dossier',
      })
    }
  }

  const isLoading = createFolder.isPending || updateFolder.isPending

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="md">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <HStack gap={2}>
              <Folder size={20} />
              <DialogTitle>
                {mode === 'create' ? 'Créer un dossier' : 'Modifier le dossier'}
              </DialogTitle>
            </HStack>
          </DialogHeader>

          <DialogBody>
            <VStack gap={4} align="stretch">
              {/* Nom du dossier */}
              <Field.Root required>
                <Field.Label>Nom du dossier</Field.Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Devis 2025"
                  disabled={isLoading}
                  autoFocus
                />
                <Field.HelperText>
                  Ce nom sera visible dans l'arborescence des dossiers
                </Field.HelperText>
              </Field.Root>

              {/* Sélecteur de dossier parent (mode création uniquement) */}
              {mode === 'create' && (
                <Field.Root>
                  <Field.Label>Emplacement</Field.Label>
                  <NativeSelectRoot position="relative">
                    <NativeSelectField
                      value={parentFolderId || ''}
                      onChange={(e) => setParentFolderId(e.target.value || undefined)}
                      paddingRight="10"
                    >
                      <option value="">Racine de la résidence</option>
                      {(() => {
                        // Build folder hierarchy for display (with indentation)
                        const buildFolderHierarchy = (
                          folders: DocumentFolder[],
                          parentId: string | null = null,
                          level: number = 0
                        ): Array<{ id: string; name: string; level: number }> => {
                          const result: Array<{ id: string; name: string; level: number }> = []
                          const children = folders.filter((f) => f.parent_folder_id === parentId)

                          children.forEach((folder) => {
                            result.push({ id: folder.id, name: folder.name, level })
                            result.push(...buildFolderHierarchy(folders, folder.id, level + 1))
                          })

                          return result
                        }

                        const folderHierarchy = buildFolderHierarchy(folders)

                        // Build display label for folders with hierarchy indentation
                        const getFolderDisplayLabel = (folder: { id: string; name: string; level: number }, level: number): string => {
                          const indent = '\u00A0'.repeat(level * 4)
                          const prefix = level > 0 ? '└─ ' : ''
                          return `${indent}${prefix}${folder.name}`
                        }

                        return folderHierarchy
                          .filter((f) => f.id !== editFolderId) // Éviter de sélectionner soi-même
                          .map((folder) => (
                            <option key={folder.id} value={folder.id}>
                              {getFolderDisplayLabel(folder, folder.level)}
                            </option>
                          ))
                      })()}
                    </NativeSelectField>
                    <Box
                      position="absolute"
                      right="3"
                      top="50%"
                      transform="translateY(-50%)"
                      pointerEvents="none"
                      color="gray.500"
                    >
                      <ChevronDown size={16} />
                    </Box>
                  </NativeSelectRoot>
                  {parentPath && (
                    <Field.HelperText>
                      Chemin complet : {parentPath}
                    </Field.HelperText>
                  )}
                </Field.Root>
              )}

              {/* Description (optionnel) */}
              <Field.Root>
                <Field.Label>Description (optionnel)</Field.Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description du dossier..."
                  disabled={isLoading}
                />
              </Field.Root>

              {/* Dossier système */}
              <Field.Root>
                <HStack justify="space-between" align="start" p={4} bg="gray.50" borderRadius="md">
                  <Box flex="1">
                    <HStack gap={2} mb={1}>
                      <Lock size={16} />
                      <Field.Label mb={0} fontWeight="semibold">
                        Dossier système
                      </Field.Label>
                      {isSystemFolder && <Badge colorScheme="blue" size="sm">Système</Badge>}
                    </HStack>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Un dossier système est associé à une catégorie et permet la suggestion automatique lors de l'upload
                    </Text>
                  </Box>
                  <Switch.Root
                    checked={isSystemFolder}
                    disabled={isLoading}
                    onCheckedChange={(details: { checked: boolean }) => {
                      setIsSystemFolder(details.checked)
                      if (!details.checked) {
                        setSuggestedCategory(undefined)
                      }
                    }}
                  >
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.HiddenInput />
                  </Switch.Root>
                </HStack>
              </Field.Root>

              {/* Sélection de la catégorie (si dossier système) */}
              {isSystemFolder && (
                <Field.Root required>
                  <Field.Label>Catégorie associée</Field.Label>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={suggestedCategory || ''}
                      onChange={(e) => setSuggestedCategory(e.target.value as DocumentCategory)}
                    >
                      <option value="">Sélectionner une catégorie...</option>
                      {CATEGORIES.map((cat) => (
                        <option
                          key={cat.value}
                          value={cat.value}
                          disabled={usedCategories.includes(cat.value)}
                        >
                          {cat.label}
                          {usedCategories.includes(cat.value) ? ' (déjà utilisée)' : ''}
                        </option>
                      ))}
                    </NativeSelectField>
                    <Box
                      position="absolute"
                      right="3"
                      top="50%"
                      transform="translateY(-50%)"
                      pointerEvents="none"
                      color="gray.500"
                    >
                      <ChevronDown size={16} />
                    </Box>
                  </NativeSelectRoot>
                  <Field.HelperText>
                    Une seule catégorie par résidence. Les documents uploadés dans ce dossier hériteront automatiquement de cette catégorie.
                  </Field.HelperText>
                </Field.Root>
              )}

              {/* Aperçu du chemin complet */}
              {mode === 'create' && (
                <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                  <HStack gap={2} mb={1}>
                    <Folder size={14} color="#3182CE" />
                    <Text fontSize="xs" fontWeight="semibold" color="blue.700">
                      Aperçu du chemin
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.700" fontFamily="mono">
                    {parentPath ? `${parentPath}/` : '/'}
                    <Text as="span" fontWeight="bold" color="blue.700">
                      {name || '(nom du dossier)'}
                    </Text>
                  </Text>
                </Box>
              )}
            </VStack>
          </DialogBody>

          <DialogFooter>
            <HStack gap={2}>
              <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                Annuler
              </Button>
              <Button
                colorPalette="brand"
                onClick={handleSubmit}
                disabled={!name.trim() || isLoading}
                loading={isLoading}
              >
                {mode === 'create' ? 'Créer' : 'Enregistrer'}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
